/**
 * @lodex-pro/web-portal/routes/ApiUsage — PREMIUM API usage (Task 18.9 / OPAPI-06).
 *
 * What the operator's own API credentials have done this month, per credential
 * and in total, plus the ceilings in force. Replaces the `api-keys` PremiumStub
 * that the sidebar's "API access" entry used to open.
 *
 * Counters come from `operator_api_credential_usage` via
 * GET /v1/operator-portal/api-usage — a table built for counting, never a scan
 * of audit rows (18.9's acceptance criterion).
 *
 * Non-PREMIUM tiers still get the stub: DEC-240 gates API CALLS at PREMIUM, so a
 * BASIC operator's credential is issued and then refused on every call, and its
 * usage screen would read zero forever. The route refuses them too (403) — this
 * check is the UX, not the gate.
 *
 * ── Two things here are deliberate, not stylistic ──────────────────────────
 *
 * Counts are STRINGS end to end and are never coerced to a number. 0112 stores
 * them as bigint on purpose and a value past 2^53 cannot survive a JS number;
 * the only arithmetic on this page is the progress bar's percentage, which goes
 * through BigInt before it becomes a float for CSS.
 *
 * "Last call" renders in the viewer's LOCAL time (DEC-004 — UTC is never shown
 * to a user). The MONTH, by contrast, is a UTC bucket and cannot be localised:
 * 18.3's quota resets at the UTC month boundary and 0112's CHECK pins
 * `period_start` to it, so a locally-bucketed month would disagree with the
 * ceiling actually being enforced. The page says so in one line rather than
 * implying local time and being quietly wrong for a day at each end.
 */

import React from 'react';
import { SortButton } from 'src/components/ui/sortable-head';
import { usePortalMe, usePortalApiUsage } from '../hooks/useOperatorPortal';
import { PortalDataError } from '../components/PortalDataError';
import { PremiumStub } from './PremiumStub';
import { portalLacks } from '../nav/portal-features';
import { ariaSort, sortRows, useUrlSort } from '../lib/url-state';

/** The last 12 months as `YYYY-MM`, newest first, in UTC. */
function recentPeriods(now: Date = new Date()): readonly string[] {
  const out: string[] = [];
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

/** `2026-10` → `October 2026`. Month NAME, so nobody has to decode a number. */
function periodLabel(period: string): string {
  const [y, m] = period.split('-');
  if (y === undefined || m === undefined) return period;
  const monthIndex = Number(m) - 1;
  const name = new Date(Date.UTC(2000, monthIndex, 1)).toLocaleString(undefined, {
    month: 'long',
    timeZone: 'UTC',
  });
  return `${name} ${y}`;
}

/**
 * An ISO instant as the viewer's local date and time (DEC-004 — never UTC).
 * `undefined` locale so the browser's own formatting wins.
 */
export function localDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Thousands separators without parsing the value into a float. */
export function groupDigits(value: string): string {
  const negative = value.startsWith('-');
  const digits = negative ? value.slice(1) : value;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return negative ? `-${grouped}` : grouped;
}

/** `used / ceiling` as a 0..100 float, computed through BigInt. */
function usedPercent(used: string, ceiling: number): number {
  if (ceiling <= 0) return 0;
  const u = BigInt(used);
  const c = BigInt(ceiling);
  if (u >= c) return 100;
  // Scale by 10,000 in integer space first, so the division that produces the
  // float happens on a small number rather than on a possibly-huge bigint.
  return Number((u * 10_000n) / c) / 100;
}

const CARD: React.CSSProperties = {
  background: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '12px 16px',
};

const TH: React.CSSProperties = { textAlign: 'left', padding: '8px 12px' };
const TH_R: React.CSSProperties = { textAlign: 'right', padding: '8px 12px' };
const TD: React.CSSProperties = { padding: '8px 12px' };
const TD_R: React.CSSProperties = { padding: '8px 12px', textAlign: 'right' };
const MUTED: React.CSSProperties = { color: 'var(--color-text-tertiary)' };

function Meter({
  used,
  ceiling,
}: {
  readonly used: string;
  readonly ceiling: number;
}): React.JSX.Element {
  const pct = usedPercent(used, ceiling);
  // Colour is a secondary cue only — the number and the title carry the meaning,
  // so this still reads for someone who cannot distinguish the hues.
  const fill =
    pct >= 90
      ? 'var(--color-danger)'
      : pct >= 70
        ? 'var(--color-warning)'
        : 'var(--color-accent-blue)';
  return (
    <div
      title={`${groupDigits(used)} of ${groupDigits(String(ceiling))} used`}
      style={{
        background: 'var(--color-bg-tertiary)',
        borderRadius: '2px',
        height: '6px',
        width: '100%',
      }}
    >
      <div style={{ background: fill, borderRadius: '2px', height: '100%', width: `${pct}%` }} />
    </div>
  );
}

/** The columns the usage table sorts by (OPEN-204); anything else in ?sort= is ignored. */
const USAGE_SORTS = ['hauler', 'credential', 'calls', 'writes', 'lastCall'] as const;

export function ApiUsage(): React.JSX.Element {
  const me = usePortalMe();
  const [period, setPeriod] = React.useState<string | undefined>(undefined);
  const { data, isLoading, isError, error } = usePortalApiUsage(period);
  // OPEN-204 — click a header to sort. The API sends every credential, by hauler (the default).
  const { sort, toggle } = useUrlSort(USAGE_SORTS, { key: 'hauler', dir: 'asc' }, []);

  // No OP_API on the plan → the same locked-feature screen the sidebar's padlock promises
  // (OPP PR 6; this read 'PREMIUM' exactly before, which locked ENTERPRISE out too).
  if (portalLacks(me.data, 'OP_API')) {
    return <PremiumStub feature="api-keys" />;
  }

  const periods = recentPeriods();
  const quota = data?.limits.monthlyQuota;
  const credentials = sortRows(data?.credentials ?? [], sort, (c, k) => {
    switch (k) {
      case 'hauler':
        return c.haulerName;
      case 'credential':
        return c.label;
      // Counts stay strings (see the header); sortRows compares digit runs numerically.
      case 'calls':
        return c.requestCount;
      case 'writes':
        return c.writeCount;
      case 'lastCall':
        return c.lastRequestAt;
    }
  });

  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '4px' }}>API usage</h1>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
        Calls made by the API credentials issued to you, for one monthly quota period. Quota periods
        run on UTC, so a month here begins and ends at 00:00 UTC — the same boundary the quota
        itself resets on.
      </p>

      <label style={{ display: 'block', marginBottom: '16px', fontSize: '13px' }}>
        <span style={{ ...MUTED, marginRight: '8px' }}>Month</span>
        <select
          aria-label="Quota period"
          value={period ?? periods[0] ?? ''}
          onChange={(e): void => {
            const next = e.target.value;
            // The newest entry IS the current month; keep it as `undefined` so
            // the request omits the param and the server decides what "now" is.
            setPeriod(next === periods[0] ? undefined : next);
          }}
          style={{
            background: 'var(--color-bg-secondary)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border-secondary)',
            borderRadius: '10px',
            padding: '6px 10px',
            fontSize: '13px',
          }}
        >
          {periods.map((p) => (
            <option key={p} value={p}>
              {periodLabel(p)}
            </option>
          ))}
        </select>
      </label>

      {isLoading && <p role="status">Loading API usage…</p>}
      {isError && <PortalDataError error={error} fallback="Could not load API usage." />}

      {data !== undefined && quota !== undefined && (
        <>
          <section
            aria-label="Usage totals"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              marginBottom: '20px',
            }}
          >
            <div style={CARD}>
              <div style={{ ...MUTED, fontSize: '12px' }}>Total calls</div>
              <div style={{ fontSize: '24px', fontVariantNumeric: 'tabular-nums' }}>
                {groupDigits(data.totals.requestCount)}
              </div>
              <div style={{ ...MUTED, fontSize: '12px' }}>in {periodLabel(data.period)}</div>
            </div>
            <div style={CARD}>
              <div style={{ ...MUTED, fontSize: '12px' }}>Of those, writes</div>
              <div style={{ fontSize: '24px', fontVariantNumeric: 'tabular-nums' }}>
                {groupDigits(data.totals.writeCount)}
              </div>
              <div style={{ ...MUTED, fontSize: '12px' }}>a write counts against both ceilings</div>
            </div>
            <div style={CARD}>
              <div style={{ ...MUTED, fontSize: '12px' }}>Active credentials</div>
              <div style={{ fontSize: '24px', fontVariantNumeric: 'tabular-nums' }}>
                {data.totals.activeCredentials}
              </div>
              <div style={{ ...MUTED, fontSize: '12px' }}>of {data.credentials.length} shown</div>
            </div>
          </section>

          <table
            aria-label="API usage by credential"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--color-bg-tertiary)' }}>
                <th style={TH} aria-sort={ariaSort(sort, 'hauler')}>
                  <SortButton column="hauler" sort={sort} onSort={toggle}>
                    Hauler
                  </SortButton>
                </th>
                <th style={TH} aria-sort={ariaSort(sort, 'credential')}>
                  <SortButton column="credential" sort={sort} onSort={toggle}>
                    Credential
                  </SortButton>
                </th>
                <th style={TH_R} aria-sort={ariaSort(sort, 'calls')}>
                  <SortButton column="calls" sort={sort} onSort={toggle}>
                    Calls
                  </SortButton>
                </th>
                <th style={TH_R} aria-sort={ariaSort(sort, 'writes')}>
                  <SortButton column="writes" sort={sort} onSort={toggle}>
                    Writes
                  </SortButton>
                </th>
                <th style={TH}>Monthly quota</th>
                <th style={TH} aria-sort={ariaSort(sort, 'lastCall')}>
                  <SortButton column="lastCall" sort={sort} onSort={toggle}>
                    Last call
                  </SortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {credentials.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '16px', textAlign: 'center', ...MUTED }}>
                    No API credentials have been issued to you yet. A hauler you work with issues
                    these.
                  </td>
                </tr>
              ) : (
                credentials.map((c) => (
                  <tr
                    key={c.credentialId}
                    style={{ borderTop: '1px solid var(--color-border-primary)' }}
                  >
                    <td style={TD}>{c.haulerName}</td>
                    <td style={TD}>
                      <div>{c.label}</div>
                      <div style={{ ...MUTED, fontSize: '12px', fontFamily: 'monospace' }}>
                        {c.keyId}
                        {c.status === 'REVOKED' && (
                          <span style={{ color: 'var(--color-danger)', marginLeft: '8px' }}>
                            revoked
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...TD_R, fontVariantNumeric: 'tabular-nums' }}>
                      {groupDigits(c.requestCount)}
                    </td>
                    <td style={{ ...TD_R, fontVariantNumeric: 'tabular-nums' }}>
                      {groupDigits(c.writeCount)}
                    </td>
                    <td style={{ ...TD, minWidth: '160px' }}>
                      <Meter used={c.requestCount} ceiling={quota.total} />
                      <div style={{ ...MUTED, fontSize: '12px', marginTop: '4px' }}>
                        {groupDigits(c.remainingRequests)} calls left
                        {' · '}
                        {groupDigits(c.remainingWrites)} writes
                      </div>
                    </td>
                    <td style={{ ...TD, ...MUTED, fontSize: '13px' }}>
                      {c.lastRequestAt === null ? 'never' : localDateTime(c.lastRequestAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <section
            aria-label="Limits in force"
            style={{ ...CARD, marginTop: '16px', fontSize: '13px' }}
          >
            <div style={{ marginBottom: '6px' }}>Limits in force</div>
            <ul style={{ ...MUTED, margin: 0, paddingLeft: '18px', lineHeight: 1.7 }}>
              <li>
                {groupDigits(String(quota.total))} calls per credential per month, of which at most{' '}
                {groupDigits(String(quota.write))} may be writes.
              </li>
              <li>
                Bursts: {data.limits.burst.read.credential.max} reads and{' '}
                {data.limits.burst.write.credential.max} writes per credential per{' '}
                {data.limits.burst.read.credential.window}, and {data.limits.burst.read.tenant.max}{' '}
                reads across all of a hauler&rsquo;s credentials.
              </li>
              <li>
                Exceeding a burst limit returns 429 and clears within the window. Exhausting the
                monthly quota also returns 429 but clears only at the next period — the{' '}
                <code>Retry-After</code> header says which.
              </li>
            </ul>
          </section>
        </>
      )}
    </main>
  );
}
